import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { fadeInAnimation, fadeInOnEnterAnimation } from 'angular-animations';
import { ChangePasswordComponent } from 'src/app/PopUps/change-password/change-password.component';
import { DeleteConfirmationComponent } from 'src/app/PopUps/delete-confirmation/delete-confirmation.component';
import { UserProfileComponent } from 'src/app/PopUps/user-profile/user-profile.component';
import { AccessTokenService } from 'src/app/Services/access-token.service';
import { AnalyticsService } from 'src/app/Services/analytics.service';
import { AlertService } from 'src/app/Services/alert.service';
import { DynamicDataService } from 'src/app/Services/dynamic-data.service';
import { SettingsService } from 'src/app/Services/settings.service';
import { UserDataProviderService } from 'src/app/Services/user-data-provider.service';
import { MaterialModule } from 'src/app/Shared/MaterialModule/material/material.module';
import { computeInitials } from 'src/app/Shared/utils/user-initials';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DEFAULT_LANG, SUPPORTED_LANGUAGES, SUPPORTED_LANG_CODES, getStoredLang, setStoredLang, isSupportedLang } from 'src/app/Shared/constants/supported-languages';
import { HEADER_HIDE_RULES, HeaderWidget } from 'src/app/Shared/constants/header-visibility';
import { MsalService } from '@azure/msal-angular';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth, { IDToken } from '@okta/okta-auth-js';
import { GlobalSearchComponent } from 'src/app/CommonComponents/global-search/global-search.component';
import { PocTierBarComponent } from 'src/app/CommonComponents/poc-tier-bar/poc-tier-bar.component';
import { MatBadgeModule } from '@angular/material/badge';
import { NotificationService, NotificationItem } from 'src/app/Services/notification.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, MaterialModule, MatBadgeModule, ReactiveFormsModule, FormsModule, TranslateModule, GlobalSearchComponent, PocTierBarComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  animations: [
    fadeInOnEnterAnimation({ delay: 500 }), fadeInAnimation()
  ],
})

export class HeaderComponent implements OnInit, OnDestroy {
  // Note: a `customStyle` ngStyle map for the avatar fallback used to live here,
  // but it was never referenced from the template. Avatar styling is now handled
  // entirely via SCSS tokens in header.component.scss / styles.scss.

  userProfile: any;
  isAnimation = false;
  isSession = false;
  isSideNavExpCol = false;
  isAdmin = false;

  pageDynamicData: any;
  separatedData: any;
  dynamicTableData: any;
  menus: any;
  username: any;
  userEmail: any;
  userInitials: any;
  profileMenuOpen = false;
  langDropdownOpen = false;
  currDropdownOpen = false;
  selectedLang = '';
  selectedCurr = '';
  // Populated from `settings/getLanguages` (see getLanguages()); the constant
  // is only a fallback if the API is unreachable. Legacy Ang8 drives the same
  // dropdown from the same endpoint, so the tenant-configured list stays in
  // parity across both apps.
  languages: ReadonlyArray<{ code: string; name: string; label: string }> = SUPPORTED_LANGUAGES;
  currencies: any[] = [];

  /** Display label for the currently-selected currency on the header button.
   *  Uses Curr_Name from the currencies array but strips the trailing symbol/code
   *  suffix (e.g. 'Euros(€)' → 'Euros') so the button reads clean; the full
   *  'Euros(€)' still appears in the dropdown options. Falls back to the code
   *  while the list is still loading or if the row is missing. */
  get selectedCurrName(): string {
    const match = this.currencies.find(c => c?.Curr_Code === this.selectedCurr);
    const raw = match?.Curr_Name ?? this.selectedCurr;
    const paren = raw.indexOf('(');
    return (paren > 0 ? raw.slice(0, paren) : raw).trim();
  }

  /** Full display name for the currently-selected language (e.g. 'English' for 'EN'),
   *  resolved from the languages array. Falls back to the uppercased code while the
   *  list is still loading. */
  get selectedLangName(): string {
    const match = this.languages.find(l => l?.code === this.selectedLang);
    return match?.name ?? (this.selectedLang || '').toUpperCase();
  }
  langFlag = false;
  currFlag = false;
  // Router.url is empty until the first NavigationEnd fires — before that, the
  // route-hide check in isWidgetHiddenByRoute() cannot know the destination.
  // Guarding the tenant-gated switchers on this flag eliminates the brief flash
  // on direct-nav to routes with hides (e.g. /view-PDF) where the tenant-gate
  // HTTP response arrives before NavigationEnd.
  urlKnown = false;
  // Notification center (US #9575): bell badge count + dropdown panel state.
  notifUnreadCount = 0;
  notifOpen = false;
  notifItems: NotificationItem[] = [];
  notifLoading = false;
  // Throttle navigation-driven bell refreshes — each refresh runs live DB queries
  // (event count + every task provider), and a SPA fires NavigationEnd constantly.
  // Producer signals (approve/reject) bypass this so post-action updates stay instant.
  // 5s window keeps the badge close to live without hammering the DB on rapid nav.
  private lastNotifRefreshMs = 0;
  private readonly NOTIF_MIN_REFRESH_MS = 5000;
  private destroy$ = new Subject<void>();
  // Built dynamically in buildDefaultProfileMenus() so the admin-only
  // "Menu Settings" entry can be inserted based on the current user's role.
  defaultProfileMenus: { Title: string; FieldName: string }[] = [];

  readonly profileMenuIcons: Record<string, string> = {
    menu1MyProfile: 'person_outline',
    menu1ChangePassword: 'lock_outline',
    menu1Settings: 'settings',
    menu1MenuSettings: 'menu_open',
    menu1Logout: 'logout',
  };

  getProfileMenuIcon(fieldName: string): string {
    return this.profileMenuIcons[fieldName] ?? 'chevron_right';
  }

  /**
   * `tblPageFields.FieldName` keys that tenant-gate the Ang18-only header
   * widgets on `HeaderPage`. Language & currency are NOT in here on purpose —
   * they gate on `tblConfiguration` flags (see below) to stay in parity with
   * Angular 8. Strings mirror the DB seed
   * (`SpendkeySql/HeaderPageScripts/SEED_HeaderPage_Switchers.sql`); a typo
   * silently hides the widget with no runtime error.
   */
  readonly HeaderPageFields = {
    Notification: 'notificationCenter',
    Search: 'globalSearch',
  } as const;

  /**
   * Language & currency parity with the legacy Angular 8 header — both apps
   * gate on `tblConfiguration` flags (`LanguageTranslation`, `CurrencyConversion`)
   * read via `SP_Get_lang_Curr_config` and cached on `langFlag`/`currFlag`. A
   * DBA flipping the settingValue to False hides the widget on both apps.
   */
  /**
   * Route-level hide layer that sits on top of every tenant gate — see
   * `HEADER_HIDE_RULES` in `Shared/constants/header-visibility.ts` for the
   * per-route hide list. Returns true when the current URL prefix is
   * configured to hide `widget`. Angular runs change detection on
   * `NavigationEnd`, so the getters re-evaluate as routes change; no
   * subscription needed here.
   */
  private isWidgetHiddenByRoute(widget: HeaderWidget): boolean {
    const url = this.routerService.url ?? '';
    const match = Object.keys(HEADER_HIDE_RULES).find(prefix => url.startsWith(prefix));
    return !!match && HEADER_HIDE_RULES[match].includes(widget);
  }

  get showLanguageSwitcher(): boolean {
    return this.urlKnown && this.langFlag && !this.isWidgetHiddenByRoute('language');
  }

  get showCurrencySwitcher(): boolean {
    return this.urlKnown && this.currFlag && !this.isWidgetHiddenByRoute('currency');
  }

  /**
   * Notification + global search are Ang18-only widgets with no legacy
   * `tblConfiguration` flag. `SP_Get_PageData` filters `WHERE pf.Status = 1`
   * server-side, so rows with Status=0 never reach the frontend — flip
   * `tblPageFields.Status` on the widget's row to toggle.
   */
  get showNotificationCenter(): boolean {
    return !!this.dynamicDataService.getStatusFieldName(
      this.HeaderPageFields.Notification, this.pageDynamicData);
  }

  get showGlobalSearch(): boolean {
    return !!this.dynamicDataService.getStatusFieldName(
      this.HeaderPageFields.Search, this.pageDynamicData);
  }

  constructor(
    private routerService: Router,
    private dialog: MatDialog,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    public accessTokenService: AccessTokenService,
    public dynamicDataService: DynamicDataService,
    private alertService: AlertService,
    private _userDataProvider: UserDataProviderService,
    private translate: TranslateService,
    private settingsService: SettingsService,
    private msalService: MsalService,
    private notificationService: NotificationService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private analytics: AnalyticsService,
  ) {
    this.userProfile = this.accessTokenService.getProfilePicture();

    this.matIconRegistry.addSvgIcon(
      'cld_ico_logout',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/new-icons/cld_ico_logout.svg'
      )
    );
  }

  ngOnInit(): void {
    const lang = getStoredLang();

    this.translate.addLangs(SUPPORTED_LANG_CODES);
    this.translate.setDefaultLang(DEFAULT_LANG);
    this.translate.use(lang);

    this.selectedLang = lang;

    this.username = this.accessTokenService.getUserName() ?? '';
    this.userEmail = this.accessTokenService.getUserEmail() ?? '';
    this.userInitials = computeInitials(this.username);

    if (this.dynamicDataService.getDataFromLocal('HeaderPage') != null) {
      this.pageDynamicData = this.dynamicDataService.getDataFromLocal('HeaderPage');
      this.separatedData = this.dynamicDataService.separateByType(this.pageDynamicData);

      this.menus = this.separatedData['Menu Label 1'];
      this.augmentMenusForAdmin();

    } else {
      this.getPageData();
    }

    this.isAnimation = false;
    this.getusertype();

    if (this.accessTokenService.isTokenAvailable()) {
      this.getLangCurrConfig();
      this.getLanguages();
      this.getCurrencyList();
      this.refreshNotifCount();
    }

    // Refresh the bell count when a producer (e.g. approve/reject) signals a change (D3).
    this.notificationService.refresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshNotifCount(true));   // producer signal — refresh immediately

    // Tab title is now set by SpendkeyTitleStrategy at app scope (see app.config.ts).
    // Header only owns the notif-count refresh + animation flag on navigation.
    this.routerService.events.pipe(takeUntil(this.destroy$)).subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.isAnimation = true;
        // Router.url is now populated — the route-hide getters can trust it.
        this.urlKnown = true;
        // Keep the bell current as the user moves around the app (D3: refresh on navigation).
        this.refreshNotifCount();
      }
    });

    this.accessTokenService.castValue.pipe(takeUntil(this.destroy$)).subscribe(
      (value) => (this.isSideNavExpCol = value)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  getusertype() {
    this.isAdmin = this.isNonMemberRole();
    this.buildDefaultProfileMenus();
  }

  private isNonMemberRole(): boolean {
    return this.accessTokenService.getUserRole() !== 'Member';
  }

  /** Whether the admin-only profile entries are offered: EXACTLY the rule their
   *  route guard admits on (Spendkey email AND Admin/SuperAdmin), so a link is
   *  never shown to someone the page would refuse. Read from the token, not a
   *  field, because the first cached-menu pass runs before getusertype() - a
   *  field read there would strip the entries from every user, admins included. */
  private canSeeAdminOnlyMenus(): boolean {
    return this.accessTokenService.isSpendkeyStaff('admin');
  }

  /**
   * Profile dropdown items. Change Password is dropped only when the login page
   * recorded an SSO-only deployment; otherwise this is the same list as before.
   * Best-effort: the backend refuses change-password there regardless.
   */
  get profileMenus(): any[] {
    const list = this.menus ?? this.defaultProfileMenus;
    if (localStorage.getItem('passwordLoginEnabled') !== 'false' || !Array.isArray(list)) {
      return list;
    }
    return list.filter((m: any) => m?.FieldName !== 'menu1ChangePassword');
  }

  /** Profile-menu entries behind `spendkeyStaffGuard('admin')`, in display order. */
  private static readonly ADMIN_ONLY_PROFILE_MENUS: ReadonlyArray<{ Title: string; FieldName: string }> = [
    { Title: 'Settings', FieldName: 'menu1Settings' },
    { Title: 'Menu Settings', FieldName: 'menu1MenuSettings' },
  ];

  private buildDefaultProfileMenus(): void {
    const menus = [
      { Title: 'My Profile', FieldName: 'menu1MyProfile' },
      { Title: 'Change Password', FieldName: 'menu1ChangePassword' },
    ];
    if (this.canSeeAdminOnlyMenus()) {
      menus.push(...HeaderComponent.ADMIN_ONLY_PROFILE_MENUS.map(m => ({ ...m })));
    }
    menus.push({ Title: 'Logout', FieldName: 'menu1Logout' });
    this.defaultProfileMenus = menus;
    // `menus` from dynamic backend config takes precedence over `defaultProfileMenus`
    // in the template, so the admin-only item must also be spliced into that array
    // once isAdmin is known.
    this.augmentMenusForAdmin();
  }

  /** Bring the backend-configured profile menu in line with the admin rule:
   *  admin-only entries are removed for anyone the guard would refuse, and added
   *  for those it admits when the tenant's config omits them. An entry the config already carries is
   *  kept as-is, so its configured Title survives. */
  private augmentMenusForAdmin(): void {
    if (!Array.isArray(this.menus)) return;
    const adminOnly = HeaderComponent.ADMIN_ONLY_PROFILE_MENUS;
    if (!this.canSeeAdminOnlyMenus()) {
      const keys = new Set(adminOnly.map(m => m.FieldName));
      this.menus = this.menus.filter((m: any) => !keys.has(m?.FieldName));
      return;
    }
    for (const entry of adminOnly) {
      if (this.menus.some((m: any) => m?.FieldName === entry.FieldName)) continue;
      const item = { ...entry };
      const logoutIdx = this.menus.findIndex((m: any) => m?.FieldName === 'menu1Logout');
      if (logoutIdx >= 0) {
        this.menus.splice(logoutIdx, 0, item);
      } else {
        this.menus.push(item);
      }
    }
  }

  loadUserData() {

    const req = {
      Organization_Id: this.accessTokenService.getOrganizationId(),
      UserId: this.accessTokenService.getUserId().toString()
    }
    this._userDataProvider.getUser(req).subscribe((response: any) => {
      if (response.status == 'success') {

        this.userProfile = response.data[0].UserProfilePic || null;
        if (this.userProfile) {
          localStorage.setItem('profilePicture', this.userProfile);
        } else {
          localStorage.removeItem('profilePicture');
        }
      } else {
      }
    });
  }

  userLogout(): void {
    const toDelete = true;
    const dialogRef = this.dialog.open(DeleteConfirmationComponent, {
      panelClass: 'modal-del-confirmation',
      disableClose: true,
      data: {
        title: this.translate.instant('Logout'),
        message: this.translate.instant('Are you sure, you want to Logout?'),
        toDelete,
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result.confirmation) {
        const isOktaLogin = localStorage.getItem('oktaLogin') === 'true';
        const isMsalLogin = this.msalService.instance.getAllAccounts().length > 0;
        this.analytics.trackLogout(isMsalLogin ? 'sso-microsoft' : isOktaLogin ? 'sso-okta' : 'password');
        if (isMsalLogin) {
          this.accessTokenService.clearTokens();
          this.msalService.logoutRedirect();
        } else if (isOktaLogin) {
          let idToken: IDToken | undefined;
          try {
            const tokens = await this.oktaAuth.tokenManager.getTokens();
            idToken = tokens.idToken;
          } catch (e) {
            console.warn('[Header] Could not retrieve idToken for SLO hint', e);
          }
          this.accessTokenService.clearTokens();
          await this.oktaAuth.signOut({ idToken })
            .catch(() => this.routeToLink('/login'));
        } else {
          this.accessTokenService.clearTokens();
          this.routeToLink('/login');
        }
      }
    });
  }

  openUserProfile() {
    const dialogRef = this.dialog.open(UserProfileComponent, {
      panelClass: 'mat-modal-css',
      width: '620px',
      disableClose: true,
      data: null,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.refreshPage) {
        // Refresh profile picture
        this.userProfile = this.accessTokenService.getProfilePicture();

        // Refresh display name and initials — getUserName() now reads the
        // localStorage override written by setLocal() on save, so no re-login needed
        this.username = this.accessTokenService.getUserName() ?? '';
        this.userEmail = this.accessTokenService.getUserEmail() ?? '';
        this.userInitials = computeInitials(this.username);
      }
    });
  }

  ChangePassword() {
    const dialogRef = this.dialog.open(ChangePasswordComponent, {
      panelClass: 'mat-modal-css',
      width: '500px',
      disableClose: true,
      data: null,
    });

    dialogRef.afterClosed().subscribe();
  }

  routeToLink(link: string) {
    this.routerService.navigateByUrl(link);
  }

  routetoSettings() {
    this.routeToLink('/settings');
  }

  getPageData() {
    const requestBody =
    {
      UserID: this.accessTokenService.getUserId(),
      pageName: 'HeaderPage'
    }
    this.dynamicDataService.getPageData(requestBody).subscribe((res: any) => {
      if (res['status'] == 'success') {
        this.pageDynamicData = res['data'];
        this.loadUserData();
        this.dynamicDataService.setDataToLocal('HeaderPage', this.pageDynamicData)
        this.separatedData = this.dynamicDataService.separateByType(this.pageDynamicData);

        this.menus = this.separatedData['Menu Label 1'];
        this.augmentMenusForAdmin();

      } else {
        this.alertService.openAlert(res['message'], 'error');
      }
    })
  }

  openJobQueue(): void {
    // ToDo / Job Queue – wire to your job queue page or panel
  }

  // ── Notification center (US #9575) ───────────────────────────────────────

  /** Re-fetch the unread count for the bell badge (user resolved server-side from the JWT). */
  refreshNotifCount(force = false): void {
    if (!this.accessTokenService.isTokenAvailable()) {
      return;
    }
    // Bell is tenant-hidden → don't run the live event-count + task-provider
    // queries. This single guard covers every caller (ngOnInit, NavigationEnd,
    // and the producer refresh$ signal), so gated-off tenants pay nothing.
    if (!this.showNotificationCenter) {
      return;
    }
    // Skip refreshes that fire within the throttle window (e.g. rapid navigation),
    // unless forced by a producer signal (approve/reject).
    const now = Date.now();
    if (!force && now - this.lastNotifRefreshMs < this.NOTIF_MIN_REFRESH_MS) {
      return;
    }
    this.lastNotifRefreshMs = now;
    this.notificationService.getUnreadCount().subscribe({
      next: (res: any) => {
        const count = res?.status === 'success' ? Number(res?.unreadCount ?? 0) : 0;
        this.notifUnreadCount = isNaN(count) ? 0 : count;
        this.notificationService.setUnreadCount(this.notifUnreadCount);
      },
      error: () => { /* leave the existing count unchanged on a transient error */ }
    });
  }

  /** Toggle the notification dropdown; loads the list when opening. */
  toggleNotifPanel(): void {
    this.notifOpen = !this.notifOpen;
    if (this.notifOpen) {
      this.langDropdownOpen = false;
      this.currDropdownOpen = false;
      this.profileMenuOpen = false;
      this.loadNotifList();
    }
  }

  closeNotifPanel(): void {
    this.notifOpen = false;
  }

  /** Load the merged notification list (tasks first, then events). */
  loadNotifList(): void {
    this.notifLoading = true;
    this.notificationService.getNotifications(1, 20).subscribe({
      next: (res: any) => {
        this.notifItems = (res?.status === 'success' && Array.isArray(res?.data)) ? res.data : [];
        this.notifLoading = false;
      },
      error: () => { this.notifItems = []; this.notifLoading = false; }
    });
  }

  /** Click a notification: mark events read, close the panel, then navigate to its link. */
  onNotifClick(item: NotificationItem): void {
    if (item?.Category === 'event' && item.Id != null && !item.IsRead) {
      // Optimistic decrement — trusted until the server rejects. We do NOT re-fetch
      // getUnreadCount after firing markRead: the POST takes ~150ms, so an eager
      // refresh reads the PRE-markRead count and overwrites our correct optimistic
      // value with the stale server one (badge flickers 5 -> 6 -> 5). Instead we
      // rely on the natural NavigationEnd / poll refreshes for eventual reconciliation,
      // and roll back on server error.
      this.applyBadgeDelta(-1);
      item.IsRead = true;
      this.notificationService.markRead(item.Id).subscribe({
        next: () => { },
        error: () => { this.applyBadgeDelta(+1); item.IsRead = false; }
      });
    }
    this.closeNotifPanel();
    if (item?.Link) {
      const [rawPath, query] = item.Link.split('?');
      // Router.navigate treats a path without a leading slash as relative to
      // the current URL, which silently no-ops when a producer emits e.g.
      // "savings-tracker?measure=42". Normalise to absolute so any producer
      // (present or future) routes correctly regardless of the current page.
      const path = rawPath.startsWith('/') ? rawPath : '/' + rawPath;
      const queryParams: { [key: string]: string } = {};
      if (query) {
        query.split('&').forEach((kv) => { const [k, v] = kv.split('='); if (k) { queryParams[k] = v; } });
      }
      this.routerService.navigate([path], { queryParams });
    }
  }

  /**
   * Dismiss a notification from the tray. Events mark read on the server and splice
   * out locally. Tasks persist a signature-based dismissal so the row hides until
   * its underlying set changes (backend auto-invalidates on drift so a new pending
   * item re-surfaces the task without any client-side book-keeping).
   *
   * Both paths update the badge OPTIMISTICALLY and hold that value — we do NOT
   * re-fetch getUnreadCount right after: markRead/dismissTask are async and an eager
   * refresh reads the pre-write state and overwrites the correct optimistic count
   * with a stale one (badge visibly flickers back UP within a second). Any natural
   * refresh (navigation, poll, panel open) reconciles later; server errors roll back.
   */
  dismissNotif(item: NotificationItem, evt: Event): void {
    evt.stopPropagation(); // don't trigger the row's onNotifClick + navigate
    if (!item) { return; }
    if (item.Category === 'event' && item.Id != null) {
      if (!item.IsRead) {
        this.applyBadgeDelta(-1);
        item.IsRead = true;
        this.notificationService.markRead(item.Id).subscribe({
          next: () => { },
          error: () => { this.applyBadgeDelta(+1); item.IsRead = false; }
        });
      }
      this.notifItems = this.notifItems.filter((i) => i !== item);
      return;
    }
    if (item.Category === 'task' && item.Type) {
      const signature = String(this.notifCount(item) ?? 0);
      const restoreItem = item;
      this.applyBadgeDelta(-1);
      this.notifItems = this.notifItems.filter((i) => i !== item);
      this.notificationService.dismissTask(item.Type, signature).subscribe({
        next: () => { },
        error: () => {
          this.applyBadgeDelta(+1);
          if (!this.notifItems.includes(restoreItem)) this.notifItems = [restoreItem, ...this.notifItems];
        }
      });
    }
  }

  /**
   * Adjust the badge count locally + broadcast to subscribers. Clamped to zero.
   * Also freezes the throttled refresh window so a NavigationEnd / poll firing
   * mid-request can't race the still-pending markRead and overwrite the optimistic
   * value with pre-write server state.
   */
  private applyBadgeDelta(delta: number): void {
    this.notifUnreadCount = Math.max(0, this.notifUnreadCount + delta);
    this.notificationService.setUnreadCount(this.notifUnreadCount);
    this.lastNotifRefreshMs = Date.now();
  }

  /**
   * Clear the tray: mark every visible event read AND dismiss every visible task.
   * Tasks are persisted with their current-count signature so they stay hidden
   * until something new arrives — the backend auto-invalidates the dismissal on
   * signature drift, so a new pending/escalated item re-surfaces the counter.
   *
   * Optimistic: badge goes to zero + rows flagged/spliced immediately for a
   * responsive feel. Rolls back the read-state (events) on server failure so
   * the user isn't left with an inconsistent tray if the server call rejects.
   */
  markAllNotifRead(): void {
    const eventItems = this.notifItems.filter((i) => i.Category === 'event');
    const taskItems = this.notifItems.filter((i) => i.Category === 'task');
    const previousReadState = eventItems.map((i) => ({ item: i, wasRead: i.IsRead }));
    const previousCount = this.notifUnreadCount;
    const previousItems = this.notifItems.slice();

    // Optimistic clear: badge to zero (via applyBadgeDelta so the throttle window
    // is frozen too — otherwise a NavigationEnd firing while markAllRead is in flight
    // would refetch the pre-write count and flicker the badge back up), events flagged
    // read, everything spliced. Task dismissals are fire-and-forget.
    this.applyBadgeDelta(-previousCount);
    eventItems.forEach((i) => { i.IsRead = true; });
    this.notifItems = [];

    taskItems.forEach((task) => {
      if (!task.Type) { return; }
      const signature = String(this.notifCount(task) ?? 0);
      this.notificationService.dismissTask(task.Type, signature).subscribe({ next: () => { }, error: () => { } });
    });

    this.notificationService.markAllRead().subscribe({
      next: () => { /* trust optimistic — no reconcile refresh (would race the write) */ },
      error: () => {
        previousReadState.forEach(({ item, wasRead }) => { item.IsRead = wasRead; });
        this.notifItems = previousItems;
        this.notifUnreadCount = previousCount;
        this.notificationService.setUnreadCount(previousCount);
      }
    });
  }

  /** Material icon for a notification by category/severity. */
  notifIcon(item: NotificationItem): string {
    if (item?.Category === 'task') {
      return 'assignment_late';
    }
    switch (item?.Severity) {
      case 'error': return 'error_outline';
      case 'warn': return 'warning_amber';
      default: return 'notifications';
    }
  }

  /** Aggregate count from a task's Params ({"count":N}); null for event rows (no count). */
  notifCount(item: NotificationItem): number | null {
    if (!item?.Params) { return null; }
    try {
      const p = JSON.parse(item.Params);
      return (typeof p?.count === 'number') ? p.count : null;
    } catch { return null; }
  }

  /** Row label with the leading count stripped (the count is shown as a chip instead). */
  notifLabel(item: NotificationItem): string {
    const title = item?.Title || '';
    if (this.notifCount(item) === null) { return title; }
    const stripped = title.replace(/^\s*\d+\s+/, '');
    return stripped ? stripped.charAt(0).toUpperCase() + stripped.slice(1) : title;
  }

  /** Friendly destination ("Savings Tracker") derived from the row's Link, for the subtext. */
  notifWhere(item: NotificationItem): string | null {
    if (!item?.Link) { return null; }
    const seg = item.Link.replace(/^\//, '').split(/[/?]/)[0];
    const map: { [k: string]: string } = {
      'my-approvals': 'Approval Center',
      'savings-tracker': 'Savings Tracker',
      reclassification: 'Reclassification',
      'admin-console': 'Admin Console',
    };
    return map[seg] || null;
  }

  /** Severity bucket for the row's rail/chip colour: 'info' | 'warn' | 'error'. */
  notifSeverity(item: NotificationItem): string {
    const s = (item?.Severity || 'info').toLowerCase();
    return (s === 'warn' || s === 'error') ? s : 'info';
  }

  toggleProfileMenu(): void {
    this.profileMenuOpen = !this.profileMenuOpen;
    if (this.profileMenuOpen) {
      this.langDropdownOpen = false;
      this.currDropdownOpen = false;
    }
  }

  closeProfileMenu(): void {
    this.profileMenuOpen = false;
  }

  toggleLangDropdown(): void {
    this.langDropdownOpen = !this.langDropdownOpen;
    if (this.langDropdownOpen) {
      this.profileMenuOpen = false;
      this.currDropdownOpen = false;
    }
  }

  closeLangDropdown(): void {
    this.langDropdownOpen = false;
  }

  changeLanguage(langCode: string): void {
    this.selectedLang = langCode;
    setStoredLang(langCode as any);
    this.translate.use(langCode);
    this.langDropdownOpen = false;
    // Legacy Ang8 parity: a full reload is what lets the embedded PowerBI
    // report pick up the new `Language_Code` filter on the next mount of
    // DashboardComponent. In-place `translate.use()` only swaps Angular
    // templates; the already-embedded iframe keeps its original filters.
    // Currency switching does the same reload for the same reason.
    window.location.reload();
  }

  toggleCurrDropdown(): void {
    this.currDropdownOpen = !this.currDropdownOpen;
    if (this.currDropdownOpen) {
      this.profileMenuOpen = false;
      this.langDropdownOpen = false;
    }
  }

  closeCurrDropdown(): void {
    this.currDropdownOpen = false;
  }

  changeCurrency(currCode: string): void {
    this.selectedCurr = currCode;
    localStorage.setItem('curr', currCode);
    this.currDropdownOpen = false;
    window.location.reload();
  }

  getLangCurrConfig(): void {
    this.settingsService.getLangCurrConfig().subscribe((res: any) => {
      if (res['status'] === 'success') {
        const data: any[] = res['data'];
        for (const setting of data) {
          if (setting.settingName === 'LanguageTranslation') {
            this.langFlag = setting.settingValue === 'True';
          }
          if (setting.settingName === 'CurrencyConversion') {
            this.currFlag = setting.settingValue === 'True';
            this.settingsService.currFlag.next(this.currFlag);
          }
          if (setting.settingName === 'DefaultLangCode' && setting.settingValue) {
            const stored = localStorage.getItem('lang');
            if (!stored) {
              this.changeLanguage(setting.settingValue);
            }
          }
          if (setting.settingName === 'DefaultCurrCode' && setting.settingValue) {
            const stored = localStorage.getItem('curr');
            if (!stored) {
              this.selectedCurr = setting.settingValue;
              localStorage.setItem('curr', setting.settingValue);
            }
          }
        }
      }
    });
  }

  getLanguages(): void {
    this.settingsService.getLanguages().subscribe({
      next: (res: any) => {
        // Any success response is authoritative — including an empty list, which
        // is the correct state for a tenant with no configured languages. Only
        // the hardcoded fallback stays if the API is unreachable (error branch).
        if (res?.status !== 'success' || !Array.isArray(res?.data)) {
          return;
        }
        this.languages = res.data
          .filter((r: any) => isSupportedLang(r?.Language_Code))
          .map((r: any) => ({
            code: r.Language_Code as string,
            name: (r.Language_Name as string) ?? r.Language_Code,
            label: (r.Language_Code as string).toUpperCase(),
          }));
      },
      error: () => { /* keep hardcoded fallback */ }
    });
  }

  getCurrencyList(): void {
    this.settingsService.getCurrencyList().subscribe((res: any) => {
      if (res['status'] === 'success') {
        this.currencies = res['data'];
        const stored = localStorage.getItem('curr');
        if (stored) {
          this.selectedCurr = stored;
        } else if (this.currencies.length > 0) {
          this.selectedCurr = this.currencies[0].Curr_Code;
        }
      }
    });
  }

  performAction(action: any) {
    if (action.FieldName == 'menu1MyProfile') {
      this.openUserProfile();
    }
    if (action.FieldName == 'menu1ChangePassword') {
      this.ChangePassword();
    }
    if (action.FieldName == 'menu1Settings') {
      this.routetoSettings();
    }
    if (action.FieldName == 'menu1MenuSettings') {
      this.routeToLink('/menu-settings');
    }
    if (action.FieldName == 'menu1Logout') {
      this.userLogout();
    }
  }
}
